import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { setTryNewToday, useNewToday } from '../../hooks/useTodayPrefs';
import { mergeSearch } from '../../navigation/legacyRedirects';

// ---------------------------------------------------------------------------
// The new Today's trial (web app v4 Phase 4.7). While someone tries it, "/"
// is Today and the old addresses for the plan and Close Day open it; for
// everyone else they're the screens they know. When the new Today becomes
// everyone's, /plan → / and /close-day → /?close=1 become permanent rows in
// navigation/legacyRedirects and the old screens go.
// ---------------------------------------------------------------------------

/** "Try the new Today", offered on the old dashboard. Switching shows Today at once. */
export function TryNewTodayOffer() {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const turnOn = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await setTryNewToday(true);
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary/25 bg-primary/5 px-4 py-2.5 text-sm">
      <p className="flex min-w-0 items-center gap-2">
        <Sparkles aria-hidden className="h-4 w-4 shrink-0 text-primary" />
        <span>There's a new Today: your brief, your plan and the close of the day, on one screen.</span>
      </p>
      <div className="flex items-center gap-2">
        {failed && <span role="alert" className="text-xs text-destructive">Couldn't switch. Try again.</span>}
        <Button size="sm" onClick={() => { void turnOn(); }} disabled={busy} className="gap-1.5">
          {busy && <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />}
          Try the new Today
        </Button>
      </div>
    </div>
  );
}

/**
 * An old address of a screen the new Today replaces: the old screen for
 * someone not trying the new Today, Today (at `to`, keeping the query and
 * hash) for someone who is.
 */
export function OldTodayOr({ old, to }: { old: React.ReactElement; to: string }) {
  const newToday = useNewToday();
  const location = useLocation();
  if (newToday === 'loading') {
    return (
      <div role="status" aria-label="Loading" className="flex min-h-[200px] items-center justify-center">
        <Loader2 aria-hidden className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!newToday) return old;
  const [pathname, query = ''] = to.split('?');
  return (
    <Navigate
      replace
      to={{ pathname, search: mergeSearch(query, location.search), hash: location.hash }}
      // Asked for the plan, so an assistant gets the executive's day, not Executives.
      state={{ openToday: true }}
    />
  );
}
